<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import eventsHttpService, { type EventRow } from '@/services/http/eventsHttpService.ts'
import EventDetailDialog from './EventDetailDialog.vue'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const { t, te, locale } = useI18n()

const eventsQuery = useQuery({
  // locale в ключе: справочники локализуются бэком по X-Language — смена языка = рефетч.
  queryKey: computed(() => ['admin', 'events', locale.value]),
  queryFn: () => eventsHttpService.getEvents(),
})

const events = computed<EventRow[]>(() => eventsQuery.data.value ?? [])

const dialogVisible = ref(false)
const selectedEventId = ref<string | null>(null)

function openDetail(id: string) {
  selectedEventId.value = id
  dialogVisible.value = true
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU')
}

// Подпись фазы — из i18n админки по коду (вычисляемое состояние, не справочник БД).
function phaseLabel(code: string): string {
  const key = `eventPhase.${code}`
  return te(key) ? t(key) : code
}

// Цвет тега фазы: активные состояния — акцентные, завершённое/анонс — нейтральные.
function phaseSeverity(code: string): string {
  switch (code) {
    case 'RegistrationOpen':
      return 'success'
    case 'InProgress':
      return 'info'
    case 'RegistrationClosed':
      return 'warn'
    case 'Finished':
      return 'secondary'
    default:
      return 'contrast'
  }
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <h1 class="text-xl font-bold">{{ t('events.title') }}</h1>

    <DataTable :value="events" :loading="eventsQuery.isLoading.value" data-key="id" class="w-full">
      <template #empty>
        <div class="text-sm text-surface-500 py-4">{{ t('events.empty') }}</div>
      </template>

      <Column :header="t('events.columns.name')">
        <template #body="{ data }">
          <Button
            link
            class="p-0"
            :label="data.name"
            data-test="event-name"
            @click="openDetail(data.id)"
          />
        </template>
      </Column>
      <Column :header="t('events.columns.startsAt')">
        <template #body="{ data }">{{ formatDate(data.startsAt) }}</template>
      </Column>
      <Column :header="t('events.columns.phase')">
        <template #body="{ data }">
          <Tag :value="phaseLabel(data.phase)" :severity="phaseSeverity(data.phase)" />
        </template>
      </Column>
      <Column :header="t('events.columns.registrationDeadline')">
        <template #body="{ data }">{{ formatDate(data.registrationDeadline) }}</template>
      </Column>
      <Column :header="t('events.columns.registration')">
        <template #body="{ data }">
          <Tag
            :value="
              data.isRegistrationOpen
                ? t('events.registration.open')
                : t('events.registration.closed')
            "
            :severity="data.isRegistrationOpen ? 'success' : 'secondary'"
          />
        </template>
      </Column>
      <Column :header="t('events.columns.isActive')">
        <template #body="{ data }">
          <Tag
            :value="data.isActive ? t('events.active.yes') : t('events.active.no')"
            :severity="data.isActive ? 'success' : 'secondary'"
          />
        </template>
      </Column>
      <Column :header="t('events.columns.registrationsCount')">
        <template #body="{ data }">{{ data.registrationsCount }}</template>
      </Column>
    </DataTable>

    <EventDetailDialog v-model:visible="dialogVisible" :event-id="selectedEventId" />
  </section>
</template>
